import React from "react";
import {
    IResourceComponentsProps,
    useTranslate
} from "@pankod/refine-core";
import {
    Avatar,
    Button,
    Create,
    Box,
    FormControl,
    FormHelperText,
    Grid,
    Stack,
    TextField,
    Typography,
    Input,
    Radio,
    RadioGroup,
    FormControlLabel,
    Backdrop,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MuiList,
    ListItem,
    ListItemText,
    IconButton,
    Select,
    MenuItem
} from "@pankod/refine-mui";
import { Delete } from "@mui/icons-material";
import { useStepsForm, Controller } from "@pankod/refine-react-hook-form";
import { IFrames, FrameError, IFrameOption } from "interfaces";
import Frame from "components/frames";
import _ from "lodash";
import { storage } from "services/firebaseService";
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { uuidv4 } from "@firebase/util";


const MAX_FILE_SIZE = 2097152; // 2MB

export interface SimpleDialogProps {
    open: boolean;
    selectedValue: string | null;
    onClose: (() => void) | null;
    onSubmit: ((frame:any) => void) | null;
}

function SimpleDialog(props: SimpleDialogProps) {
    const { onClose, onSubmit, selectedValue, open } = props;
    const txtText = React.useRef<any>(null);
    const txtFont = React.useRef<any>(null);
    const txtFontSize = React.useRef<any>(null);
    const txtColor = React.useRef<any>(null);
    const txtType = React.useRef<any>(null);

    const handleClose = () => {
        onClose && onClose();
    };

    const handleSubmit = () => {
        const valText = txtText.current?.value;
        const valFont = txtFont.current?.value;
        const valFontSize = txtFontSize.current?.value;
        const valColor= txtColor.current?.value;
        const type = txtType.current.value;
        onSubmit && onSubmit({
            text: valText,
            font: valFont,
            fontSize: valFontSize,
            color: valColor,
            type
        });
        onClose && onClose();
    };

    return (
        <Dialog onClose={handleClose} open={open}>
            <DialogTitle>Add New Label</DialogTitle>
            <DialogContent>
                <Stack gap="24px" sx={{
                    padding: "10px"
                }}>
                    <FormControl>
                        <Select
                            inputRef={txtType}
                            defaultValue="text"
                            placeholder="Select Type"
                            fullWidth
                            >
                            <MenuItem value="text"> Text </MenuItem>
                            <MenuItem value="rect"> Rect </MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl>
                        <TextField
                            inputRef={txtText}
                            size="medium"
                            variant="outlined"
                            required
                            label="Text"
                        />
                    </FormControl>
                    <FormControl>
                        <TextField
                            inputRef={txtFont}
                            size="medium"
                            variant="outlined"
                            required
                            label="Font"
                        />
                    </FormControl>
                    <FormControl>
                        <TextField
                            inputRef={txtFontSize}
                            size="medium"
                            variant="outlined"
                            required
                            label="Font Size"
                        />
                    </FormControl>
                    <FormControl>
                        <TextField
                            inputRef={txtColor}
                            size="medium"
                            variant="outlined"
                            required
                            label="Color"
                        />
                    </FormControl>
                </Stack>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSubmit}>OK</Button>
                </DialogActions>
            </DialogContent>
        </Dialog>
    );
}

export const FrameCreate: React.FC<IResourceComponentsProps> = () => {
    const t = useTranslate();
    const [format, setFormat] = React.useState("portrait");
    const [labelsRegion, setLabelsRegion] = React.useState<IFrameOption[]>([]);
    const [openDialog, setOpenDialog] = React.useState(false);

    // Upload file
    const upload = async ( image:File )=>{
        if(image == null)
          return;
        const storageRef = ref(storage, uuidv4() + image.name);
        return uploadBytes(storageRef, image).then((snapshot) => {
            return getDownloadURL(snapshot.ref);
        });
      }

    const {
        refineCore: { onFinish, formLoading },
        register,
        control,
        setValue,
        getValues,
        setError,
        clearErrors,
        formState: { errors }
    } = useStepsForm<IFrames, FrameError, IFrames>({
        stepsProps: {
            isBackValidate: false,
        },
        warnWhenUnsavedChanges: true
    });
    // Thumbnail
    const [selectedFile, setSelectedFile] = React.useState<any>();
    const [preview, setPreview] = React.useState<any>();
    // Frame Image
    const [selectedFrameFile, setSelectedFrameFile] = React.useState<any>();
    const [previewFrame, setPreviewFrame] = React.useState<any>();

    // create a preview as a side effect, whenever selected file is changed
    React.useEffect(() => {
        if (!selectedFile) {
            setPreview(undefined);
            return;
        }

        const objectUrl = URL.createObjectURL(selectedFile);
        setPreview(objectUrl);

        // free memory when ever this component is unmounted
        return () => URL.revokeObjectURL(objectUrl);
    }, [selectedFile]);

    const onChangeHandler = async (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        if (!e.target.files || e.target.files.length === 0) {
            setSelectedFile(undefined);
            return;
        }

        const file = e.target.files[0];
        // Validate Size        	
        if (file.size > MAX_FILE_SIZE) {
            setError("thumbnailUrl", {
                message: "File is bigger than 2MB"
            });
            setSelectedFile(undefined);
            return;
        }
        clearErrors("thumbnailUrl");
        setSelectedFile(file);
    };

    React.useEffect(() => {
        if (!selectedFrameFile) {
            setPreviewFrame(undefined);
            return;
        }

        const objectUrl = URL.createObjectURL(selectedFrameFile);
        setPreviewFrame(objectUrl);
        // free memory when ever this component is unmounted
        return () => URL.revokeObjectURL(objectUrl);
    }, [selectedFrameFile]);

    const onFrameChangeHandler = async (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        if (!e.target.files || e.target.files.length === 0) {
            setSelectedFrameFile(undefined);
            return;
        }

        const file = e.target.files[0];
        // Validate Size        	
        if (file.size > MAX_FILE_SIZE) {
            setError("imageUrl", {
                message: "File is bigger than 2MB"
            });
            setSelectedFrameFile(undefined);
            return;
        }
        clearErrors("imageUrl");
        setSelectedFrameFile(file);
    };

    const handleClickOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    }

    const handleSubmitDialog = (val:any) => {
        const lblFrame:IFrameOption = {
            id: uuidv4(),
            text: val.text,
            font: val.font,
            fontSize: val.fontSize,
            position: {
                x: 10,
                y: 10
            },
            color: val.color,
            type: val.type,
            size: val.type == "rect" ? {
                width: 100,
                height: 100
            } : null,
            rotation: null
        }

        setLabelsRegion([
            ...labelsRegion,
            lblFrame
        ]);
    }

    const onChangeFrameAttrs = (id:string, attrs:any) => {
        const item = _.find(labelsRegion, { id });
        if (item) {
            item.position.x = attrs.x;
            item.position.y = attrs.y;

            if (item.type == "rect") {
                console.log(attrs);
                item.size = {
                    width: attrs.width,
                    height: attrs.height
                };
                item.rotation = attrs.rotation;
            }
        }
    }

    const onDeleteLayerFrame = (index:number) => {
        labelsRegion.splice(index,1);
       setLabelsRegion([
        ...labelsRegion
       ]);
    }

    const handleCreateFrame =  async () => {
        
        const [thumbnailUrl, frameUrl] = await Promise.all([
            upload(selectedFile),
            upload(selectedFrameFile)
        ]);
        // image url
        setValue('thumbnailUrl', thumbnailUrl ?? "");
        setValue('imageUrl', frameUrl ?? "");

        // set values options
        setValue('options', labelsRegion);

        onFinish(getValues());
    }

    const renderForm = () => {
        return (
            <>
                <Grid
                    container
                    sx={{
                        marginX: { xs: "0px" },
                    }}
                >
                    <Grid item xs={12}>
                        <Grid container>
                            <Grid item paddingX={4} xs={12}>
                                <Stack gap="24px">
                                    <FormControl>
                                        <TextField
                                            {...register("title", {
                                                required: t(
                                                    "errors.required.field",
                                                    {
                                                        field: "Title",
                                                    },
                                                ),
                                                maxLength: {
                                                    value: 100,
                                                    message: t(
                                                        "errors.required.max",
                                                        { max: 100 },
                                                    ),
                                                },
                                            })}
                                            size="medium"
                                            variant="outlined"
                                            required
                                            label="Title (required)"
                                        />
                                        {errors.title && (
                                            <FormHelperText error>
                                                {errors.title.message}
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                    <FormControl>
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Category
                                        </Typography>
                                        <Controller
                                            control={control}
                                            name="category"
                                            defaultValue={"themes"}
                                            render={({ field }) => (
                                                <RadioGroup
                                                    {...field}
                                                    onChange={(e, v) => {
                                                        setValue("category", v);
                                                    }}
                                                >
                                                    <FormControlLabel value={"themes"} control={<Radio />} label="Themes" />
                                                    <FormControlLabel value={"shield"} control={<Radio />} label="Shield Mode" />
                                                    <FormControlLabel value={"pause"} control={<Radio />} label="Pause Image" />
                                                </RadioGroup>

                                            )}
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Format
                                        </Typography>
                                        <Controller
                                            control={control}
                                            name="format"
                                            defaultValue={"portrait"}
                                            render={({ field }) => (
                                                <RadioGroup
                                                    {...field}
                                                    onChange={(e, v) => {
                                                        setValue("format", v);
                                                        setFormat(v);
                                                    }}
                                                >
                                                    <FormControlLabel value={"portrait"} control={<Radio />} label="Portrait" />
                                                    <FormControlLabel value={"landscape"} control={<Radio />} label="Landscape" />
                                                </RadioGroup>

                                            )}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Thumbnail
                                        </Typography>
                                        <Box sx={{
                                            height: 84,
                                            width: 153,
                                            border: '1px dashed grey'
                                        }} >
                                            <label htmlFor="avatar-input">
                                                <Input
                                                    id="avatar-input"
                                                    type="file"
                                                    sx={{
                                                        display: "none",
                                                    }}
                                                    onChange={onChangeHandler}
                                                />
                                                <input
                                                    id="file"
                                                    type="hidden"
                                                    {...register("thumbnailUrl")}
                                                />
                                                <Avatar
                                                    variant="square"
                                                    sx={{
                                                        cursor: "pointer",
                                                        width: {
                                                            xs: "153px",
                                                        },
                                                        height: {
                                                            xs: "84px",
                                                        },
                                                    }}
                                                    src={preview}
                                                    alt="Thumbnail"
                                                />
                                            </label>
                                        </Box>
                                        {errors.thumbnailUrl && (
                                            <FormHelperText error>
                                                {errors.thumbnailUrl.message}
                                            </FormHelperText>
                                        )}
                                    </FormControl>

                                    <FormControl>
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Frame
                                        </Typography>
                                        <Stack direction="row" gap={5}>
                                            <Box>
                                                <label className="btn" htmlFor="frame-input">
                                                    <Input
                                                        id="frame-input"
                                                        type="file"
                                                        sx={{
                                                            display: "none",
                                                        }}
                                                        onChange={onFrameChangeHandler}
                                                    />
                                                    <input
                                                        id="file"
                                                        type="hidden"
                                                        {...register("imageUrl")}
                                                    />
                                                    <Button onClick={(event)=> {
                                                        event.preventDefault();
                                                        document.getElementById('frame-input')?.click();
                                                    }} fullWidth variant="outlined" sx={{ my: "8px", textTransform: "none"}}>
                                                        Upload Image
                                                    </Button>
                                                </label>
                                                
                                                <Button onClick={handleClickOpenDialog} fullWidth variant="outlined" sx={{ my: "8px", textTransform: "none", }}>
                                                    Add New Label
                                                </Button>
                                                <MuiList>
                                                    { labelsRegion.map((item, index) => 
                                                        <ListItem key={index} disableGutters secondaryAction={
                                                            <IconButton onClick={onDeleteLayerFrame.bind(this, index)} edge="end" aria-label="delete">
                                                              <Delete />
                                                            </IconButton>
                                                          }>
                                                            <ListItemText>{item.text}</ListItemText>
                                                        </ListItem>
                                                        ) }
                                                </MuiList>
                                            </Box>
                                            <Frame onChangeLayer={onChangeFrameAttrs} imageUrl={previewFrame} format={format} layers={labelsRegion} />
                                        </Stack>
                                    </FormControl>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </>
        );
    };

    return (
        <Create
            title={<Typography variant="h5">Create Frame</Typography>}
            isLoading={formLoading}
            saveButtonProps={{ onClick: handleCreateFrame }}
        >
            <Box
                component="form"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                }}
                autoComplete="off"
            >
                <br />
                {renderForm()}
                <Backdrop
                    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={formLoading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            </Box>
            <SimpleDialog 
                open={openDialog} 
                selectedValue={null} 
                onClose={handleCloseDialog} 
                onSubmit={handleSubmitDialog} 
            />
        </Create>
    );
};
